'use client';

import React, { useMemo } from 'react';

import FilterFooterActions from '@/components/ui/Button/FilterFooter';
import FilterCheckboxItem from '@/components/atoms/Button/FilterCheckboxItem/FilterCheckboxItem';
import { SPEC_OPTIONS } from '@/features/search';

interface SpecialityFilterListProps {
  selectedSpecialities: string[];
  onToggle: (slug: string) => void;
  onClear: () => void;
  onApply: () => void;
}

/** "All Specialities" pseudo-option — checked when nothing is selected. */
const ALL_SPECIALITIES_OPTION = {
  slug: 'all-specialities',
  title: 'All Specialities',
};

function isOptionChecked(selectedSpecialities: string[], slug: string) {
  if (slug === ALL_SPECIALITIES_OPTION.slug) {
    return selectedSpecialities.length === 0;
  }
  return selectedSpecialities.includes(slug);
}

function handleOptionToggle(
  slug: string,
  onToggle: (slug: string) => void,
  onClear: () => void,
) {
  if (slug === ALL_SPECIALITIES_OPTION.slug) {
    onClear();
    return;
  }
  onToggle(slug);
}

/** Checkbox list shared by the mobile bottom sheet and desktop panel. */
export default function SpecialityFilterList({
  selectedSpecialities,
  onToggle,
  onClear,
  onApply,
}: SpecialityFilterListProps) {
  const options = useMemo(() => [ALL_SPECIALITIES_OPTION, ...SPEC_OPTIONS], []);

  return (
    <>
      <div className="max-h-64 overflow-y-auto pr-1">
        {options.map((opt, index) => (
          <React.Fragment key={opt.slug}>
            {index === 1 && <div className="my-2 border-t border-gray-100" />}
            <FilterCheckboxItem
              option={opt}
              checked={isOptionChecked(selectedSpecialities, opt.slug)}
              onToggle={(slug) => handleOptionToggle(slug, onToggle, onClear)}
            />
          </React.Fragment>
        ))}
      </div>
      <FilterFooterActions onClear={onClear} onApply={onApply} />
    </>
  );
}
